import React from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import {
  PopulateSupportUserDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  useMixpanelTrack,
} from 'core/hooks';
import {
  environment,
} from 'env';

type Props = {
  user: PopulateSupportUserDTO | null;
  organizationName: string;
  isLoading: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
};

type ImpersonationUserCardProps = {
  organizationName: string;
  userName: string;
  userEmail: string;
};

export function ImpersonationUserCard({
  userName,
  userEmail,
  organizationName,
}: ImpersonationUserCardProps) {
  return (
    <div className="bg-gray-50 border border-gray-100/80 rounded-lg p-4 flex gap-3">
      <div className="flex-shrink-0">
        <div className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center">
          <PersonOutlineIcon className="text-gray-600 !w-5 !h-5" />
        </div>
      </div>
      <div className="flex flex-col gap-1 min-w-0">
        <h3 className="text-sm font-semibold text-slate-900">
          {userName}
        </h3>
        {notEmptyString(userEmail) && (
        <div className="flex items-center gap-1.5 text-xs text-gray-700">
          <EmailOutlinedIcon className="text-gray-500 !w-4 !h-4" />
          <span>{userEmail}</span>
        </div>
        )}
        {notEmptyString(organizationName) && (
          <div className="flex items-center gap-1.5 text-xs text-gray-700">
            <GroupsOutlinedIcon className="text-gray-500 !w-4 !h-4" />
            <span>{organizationName}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ImpersonateDialog({
  user,
  organizationName,
  isLoading,
  error,
  onConfirm,
  onClose,
}: Props) {
  const mixpanelTrack = useMixpanelTrack(environment === 'qa');
  return !isNil(user) ? (
    <Dialog
      open
      onClose={onClose}
      title={(
        <h2 className="text-lg font-semibold">
          Confirm Impersonation
        </h2>
      )}
      className="!w-[30rem]"
      maxWidth="sm"
    >
      <div className="flex flex-col gap-4">
        <ImpersonationUserCard
          userName={user.fullName}
          userEmail={user.email}
          organizationName={organizationName}
        />
        <div className="bg-amber-50/50 border border-amber-100/80 rounded-lg p-4 flex gap-3">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-lg border border-amber-100 bg-white flex items-center justify-center">
              <WarningAmberRoundedIcon className="text-amber-700 !w-6 !h-6" />
            </div>
          </div>
          <div className="flex flex-col gap-2 min-w-0">
            <h4 className="text-sm font-semibold text-slate-900">
              What happens next
            </h4>
            <ul className="flex flex-col gap-1.5 text-xs text-gray-700">
              <li className="flex gap-2">
                <span className="flex-shrink-0">•</span>
                <span>You&apos;ll see the app exactly as this user does.</span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0">•</span>
                <span>
                  Any changes you make will affect the user&apos;s account.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0">•</span>
                <span>You can exit impersonation at any time.</span>
              </li>
            </ul>
          </div>
        </div>

        {notEmptyString(error) && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="flex gap-4 pt-2 justify-end">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              mixpanelTrack('impersonate_user_start', {
                user_id: user?.userId,
                user_email: user?.email,
                user_name: user?.fullName,
                organization_name: organizationName,
              });
              onConfirm();
            }}
            disabled={isLoading}
            className="flex-1 !bg-amber-600 hover:!bg-amber-700 !text-white !px-6"
          >
            {isLoading ? 'Starting...' : 'Impersonate user'}
          </Button>
        </div>
      </div>
    </Dialog>
  ) : null;
}
